import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Slider } from './ui/slider';
import { TrendingUp, Euro, Percent, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ROICalculator = () => {
  const { t } = useTranslation();
  const [purchasePrice, setPurchasePrice] = useState(8500000);
  const [occupancyRate, setOccupancyRate] = useState(68);
  const [averageDailyRate, setAverageDailyRate] = useState(105);
  const [operatingCosts, setOperatingCosts] = useState(35);
  const [developmentInvestment, setDevelopmentInvestment] = useState(0);

  const calculations = useMemo(() => {
    // Hotel Revenue Calculations
    const rooms = 42;
    const daysPerYear = 365;
    const hotelRevenue = rooms * daysPerYear * (occupancyRate / 100) * averageDailyRate;
    
    // Total Revenue (Hotel only - no restaurant)
    const totalRevenue = hotelRevenue;
    
    // Operating Costs
    const totalOperatingCosts = totalRevenue * (operatingCosts / 100);
    
    // EBITDA
    const ebitda = totalRevenue - totalOperatingCosts;
    const ebitdaMargin = (ebitda / totalRevenue) * 100;
    
    // ROI
    const totalInvestment = purchasePrice + developmentInvestment;
    const roi = (ebitda / totalInvestment) * 100;
    
    // Payback Period
    const paybackPeriod = totalInvestment / ebitda;
    
    return {
      hotelRevenue,
      totalRevenue,
      totalOperatingCosts,
      ebitda,
      ebitdaMargin,
      roi,
      paybackPeriod
    };
  }, [purchasePrice, occupancyRate, averageDailyRate, operatingCosts, developmentInvestment]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <Card className="border-slate-200 shadow-xl">
        <CardHeader className="bg-gradient-to-r from-[#0A2540] to-[#2C5F66] text-white">
          <CardTitle className="text-2xl flex items-center">
            <TrendingUp className="w-6 h-6 mr-3" />
            {t('calculator.title')}
          </CardTitle>
          <p className="text-slate-200 text-sm mt-2">
            {t('calculator.subtitle')}
          </p>
        </CardHeader>
        <CardContent className="p-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Input Section */}
            <div className="space-y-6">
              <div>
                <Label className="text-base font-semibold text-[#0A2540] mb-3 block">{t('calculator.purchasePrice')}</Label>
                <Input 
                  type="number"
                  value={purchasePrice}
                  onChange={(e) => setPurchasePrice(Number(e.target.value))}
                  className="text-lg"
                />
                <p className="text-sm text-slate-500 mt-1">{formatCurrency(purchasePrice)}</p>
              </div>

              <div>
                <Label className="text-base font-semibold text-[#0A2540] mb-3 block">
                  {t('calculator.occupancy')}: {occupancyRate}%
                </Label>
                <Slider 
                  value={[occupancyRate]}
                  onValueChange={(value) => setOccupancyRate(value[0])}
                  min={40}
                  max={90}
                  step={1}
                  className="py-4"
                />
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>40%</span>
                  <span>90%</span>
                </div>
              </div>

              <div>
                <Label className="text-base font-semibold text-[#0A2540] mb-3 block">
                  {t('calculator.avgRate')}: {averageDailyRate}€
                </Label>
                <Slider 
                  value={[averageDailyRate]}
                  onValueChange={(value) => setAverageDailyRate(value[0])}
                  min={80}
                  max={180}
                  step={5}
                  className="py-4"
                />
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>80€</span>
                  <span>180€</span>
                </div>
              </div>

              <div>
                <Label className="text-base font-semibold text-[#0A2540] mb-3 block">
                  {t('calculator.opCosts')}: {operatingCosts}%
                </Label>
                <Slider 
                  value={[operatingCosts]}
                  onValueChange={(value) => setOperatingCosts(value[0])}
                  min={25}
                  max={50}
                  step={1}
                  className="py-4"
                />
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>25%</span>
                  <span>50%</span>
                </div>
              </div>

              <div>
                <Label className="text-base font-semibold text-[#0A2540] mb-3 block">
                  {t('calculator.devInvestment')}
                </Label>
                <Input 
                  type="number"
                  value={developmentInvestment}
                  onChange={(e) => setDevelopmentInvestment(Number(e.target.value))}
                  className="text-lg"
                />
                <p className="text-sm text-slate-500 mt-1">{formatCurrency(developmentInvestment)}</p>
              </div>
            </div>

            {/* Results Section */}
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-6 border border-slate-200">
                <div className="flex items-center mb-2">
                  <Euro className="w-5 h-5 text-[#2C5F66] mr-2" />
                  <span className="text-sm font-medium text-slate-600">{t('calculator.totalRevenue')}</span>
                </div>
                <p className="text-3xl font-bold text-[#0A2540]">{formatCurrency(calculations.totalRevenue)}</p>
                <div className="mt-3 text-xs text-slate-500 space-y-1">
                  <div className="flex justify-between">
                    <span>{t('calculator.hotel')}:</span>
                    <span className="font-medium">{formatCurrency(calculations.hotelRevenue)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-[#C9A961]/10 to-[#D4AF37]/10 rounded-xl p-6 border border-[#C9A961]/30">
                <div className="flex items-center mb-2">
                  <TrendingUp className="w-5 h-5 text-[#C9A961] mr-2" />
                  <span className="text-sm font-medium text-slate-600">{t('calculator.ebitda')}</span>
                </div>
                <p className="text-3xl font-bold text-[#0A2540]">{formatCurrency(calculations.ebitda)}</p>
                <p className="text-sm text-slate-600 mt-1">{t('calculator.margin')}: {calculations.ebitdaMargin.toFixed(1)}%</p>
              </div>

              <div className="bg-gradient-to-br from-[#2C5F66]/10 to-[#4A7C85]/10 rounded-xl p-6 border border-[#2C5F66]/30">
                <div className="flex items-center mb-2">
                  <Percent className="w-5 h-5 text-[#2C5F66] mr-2" />
                  <span className="text-sm font-medium text-slate-600">{t('calculator.roi')}</span>
                </div>
                <p className="text-3xl font-bold text-[#0A2540]">{calculations.roi.toFixed(2)}%</p>
                <p className="text-sm text-slate-600 mt-1">{t('calculator.annually')}</p>
              </div>

              <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-6 border border-slate-200">
                <div className="flex items-center mb-2">
                  <Calendar className="w-5 h-5 text-[#0A2540] mr-2" />
                  <span className="text-sm font-medium text-slate-600">{t('calculator.payback')}</span>
                </div>
                <p className="text-3xl font-bold text-[#0A2540]">{calculations.paybackPeriod.toFixed(1)} {t('calculator.years')}</p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <p className="text-xs text-blue-800 leading-relaxed">
                  <strong>{t('calculator.noteTitle')}:</strong> {t('calculator.noteText')}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ROICalculator;